import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import DocumentTypeManager from '../admin/DocumentTypeManager';

export default function Documents({ member }) {
  const [docs, setDocs] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoad] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [deleting, setDel] = useState(null);
  const [showTypeManager, setShowTypeManager] = useState(false);
  const fileRef = useRef();

  const loadDocTypes = async () => {
    try {
      const { data } = await api.get('/meta/document-types');
      setDocTypes(data);
    } catch {
      toast.error('Failed to load document types');
    }
  };

  useEffect(() => {
    loadDocTypes();
  }, []);

  const handleTypeManagerClose = () => {
    setShowTypeManager(false);
    loadDocTypes();
  };

  useEffect(() => {
    if (!member?.id) return;
    setLoad(true);
    api.get(`/documents/${member.id}`)
      .then(r => setDocs(r.data))
      .catch(() => toast.error('Failed to load documents'))
      .finally(() => setLoad(false));
  }, [member?.id]);

  const handleFileSelect = async (file) => {
    if (!selectedType) {
      toast.error('Select a document type first');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('document_type_id', selectedType);
      const { data } = await api.post(`/documents/${member.id}/upload`, fd);
      setDocs(p => [data, ...p]);
      setSelectedType('');
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this document?')) return;
    setDel(id);
    try {
      await api.delete(`/documents/${id}`);
      setDocs(p => p.filter(d => d.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setDel(null);
    }
  };

  if (!member?.id) return (
    <div className="empty-state"><p>Save Personal info first to manage documents.</p></div>
  );

  return (
    <div>
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Upload Document</h3>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setShowTypeManager(true)}
          >
            ⚙️ Manage Types
          </button>
        </div>
        <div className="grid-2" style={{ alignItems: 'flex-end' }}>
          <div className="form-group">
            <label className="form-label">Document Type *</label>
            <select
              className="form-select"
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              disabled={uploading}
            >
              <option value="">— Select —</option>
              {docTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              style={{ display: 'none' }}
              onChange={e => e.target.files[0] && handleFileSelect(e.target.files[0])}
              disabled={uploading}
            />
            <button
              className="btn btn-primary"
              onClick={() => fileRef.current?.click()}
              disabled={!selectedType || uploading}
              style={{ width: '100%' }}
            >
              {uploading ? 'Uploading…' : 'Select File'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : docs.length === 0 ? (
        <div className="empty-state"><p>No documents uploaded yet.</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>Document Type</th>
                <th>Filename</th>
                <th>Uploaded Date</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id}>
                  <td><span className="badge badge-blue">{doc.document_type}</span></td>
                  <td>
                    <a
                      href={`https://ems.ourschool.lk/uploads/${doc.file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--primary)', textDecoration: 'none' }}
                    >
                      {doc.file_path}
                    </a>
                  </td>
                  <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}</td>
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      disabled={deleting === doc.id}
                      onClick={() => handleDelete(doc.id)}
                    >
                      {deleting === doc.id ? '…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showTypeManager && <DocumentTypeManager onClose={handleTypeManagerClose} />}
    </div>
  );
}
